import { notification } from 'antd';
import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { BASE_ENDPOINT, US_REGION } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { getCurrentUser } from '../Login/login.helper';

const { logError } = RaygunErrorHandlerService();

export type ResetPasswordSliceType = {
  tokenFromPath: string | null;
  loadingResetPassword: boolean;
};

const initialState: ResetPasswordSliceType = {
  tokenFromPath: null,
  loadingResetPassword: false,
};

export const checkToken = createAsyncThunk<boolean, undefined, AsyncThunkConfig>(
  'resetPassword/checkToken',
  async (_, thunkAPI) => {
    try {
      const token = thunkAPI.getState().resetPasswordSlice.tokenFromPath;
      if (token) {
        const currentUser = getCurrentUser(token);
        const baseURL = currentUser.region === US_REGION ? BASE_ENDPOINT.US : BASE_ENDPOINT.CA;
        const response = await axios.post(
          'v2_token_verify',
          {},
          {
            baseURL,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        return response as unknown as boolean;
      }

      return false;
    } catch (e) {
      logError(e, ['resetPasswordSlice', 'checkToken']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const sendPassword = createAsyncThunk<string, string, AsyncThunkConfig>(
  'resetPassword/sendPassword',
  async (password, thunkAPI) => {
    try {
      const token = thunkAPI.getState().resetPasswordSlice.tokenFromPath;
      if (token) {
        const currentUser = getCurrentUser(token);
        const baseURL = currentUser.region === US_REGION ? BASE_ENDPOINT.US : BASE_ENDPOINT.CA;
        const response = await axios.post(
          'v2_password_reset',
          { password },
          {
            baseURL,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        return response as unknown as string;
      }

      return '';
    } catch (e) {
      logError(e, ['resetPasswordSlice', 'sendPassword']);
      notification.error({
        title: 'Error',
        description: 'Password not updated.',
      });
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const resetPasswordSlice = createSlice({
  name: 'resetPassword',
  initialState,
  reducers: {
    updateTokenFromPath: (state, action: PayloadAction<string | null>) => {
      state.tokenFromPath = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendPassword.pending, (state, _action) => {
        state.loadingResetPassword = true;
      })
      .addCase(sendPassword.fulfilled, (state, _action) => {
        state.loadingResetPassword = false;
      })
      .addCase(sendPassword.rejected, (state, _action) => {
        state.loadingResetPassword = false;
      });
  },
});

export const { updateTokenFromPath } = resetPasswordSlice.actions;
